import { useCallback, useEffect } from 'react';
import {
    Avatar,
    Collapse,
    IconButton,
    ListItem,
    ListItemAvatar,
    ListItemText,
    makeStyles,
    Tooltip
} from '@material-ui/core';
import { Refresh, WarningRounded } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ADD_SCAN } from '../../graphql/mutations/scan';
import { SCAN_RESULT_STATUS } from '../../constants/scan-result-status';
import AssetAvatar from '../card-navigation/AssetAvatar';

export default function ScannedTag({
    tag = '',
    sessionId,
    onSaved,
    hideAfterSave = false,
    hiddenIfUnknown = false
}) {
    const classes = useStyles();
    const { t } = useTranslation();
    const [addScan, { loading, data, error, called }] = useMutation(ADD_SCAN);
    const scanResult = data?.addScan || {};
    const scan = scanResult.scan || {};

    const addScanEntry = useCallback(async () => {
        try {
            var res = await addScan({
                variables: {
                    scan: {
                        scanSessionId: sessionId,
                        tagNumber: tag
                    }
                }
            });

            if (typeof onSaved === 'function') {
                const scanResult = res.data?.addScan;
                onSaved(scanResult);
            }
        } catch {}
    }, [addScan, onSaved, sessionId, tag]);

    useEffect(() => {
        if (!called) {
            addScanEntry();
        }
    }, [addScanEntry, called]);

    let isUnknownTag = false;
    let primaryText = '';
    if (loading) {
        primaryText = <Skeleton component='p' width={150} />;
    } else if (error) {
        primaryText = t('Scan.Failed');
    } else if (
        scanResult.status === SCAN_RESULT_STATUS.ASSET_FOUND ||
        scanResult.status === SCAN_RESULT_STATUS.ASSET_FOUND_BUT_MOVED
    ) {
        primaryText = scan.asset.name || t(`ScanResult.${scanResult.status}`);
    } else {
        primaryText = scanResult.status ? t(`ScanResult.${scanResult.status}`) : '';
        isUnknownTag = true;
    }

    const hide = (hideAfterSave && scan.id && scan.asset?.id) || (hiddenIfUnknown && isUnknownTag);

    return (
        <Collapse in={!hide} appear>
            <ListItem divider>
                <ListItemAvatar>
                    {loading ? (
                        <Skeleton variant='rect' width={40} height={40} />
                    ) : scan.asset ? (
                        <AssetAvatar asset={scan.asset ?? {}} />
                    ) : (
                        <Avatar alt={primaryText} variant='rounded' className={classes.icon}>
                            <WarningRounded color='action' />
                        </Avatar>
                    )}
                </ListItemAvatar>
                <ListItemText primary={primaryText} secondary={tag} />
                {!!error && (
                    <Tooltip title={t('Scan.Retry')}>
                        <IconButton onClick={addScanEntry}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                )}
            </ListItem>
        </Collapse>
    );
}

const useStyles = makeStyles(theme => ({
    icon: {
        backgroundColor: theme.palette.background.default
    }
}));
